import React, {
  CSSProperties,
  memo,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import Button from "../Button/Button";
import clsx from "clsx";
import { MdMoreHoriz } from "react-icons/md";
import { motion, useReducedMotion } from "framer-motion";
import { createPortal } from "react-dom";
import { ICON_SIZES } from "src/constants/icons";
import useMobile from "src/hooks/useMobile";
import { EButtonKind, IButton } from "../Button/Button.types";

type TResolvedMenuPosition = "bottom" | "top";

export interface IButtonWithDropdown extends Omit<IButton, "onClick"> {
  options: Array<IButton & { keepOpenOnClick?: boolean }>;
  menuPosition?: "bottom" | "top" | "auto";
  onClick?: (e: any) => void;
  columns?: number;
  mobileColumns?: number;
  active?: boolean;
  highlight?: boolean;
  children?: ReactNode;
  kind?: EButtonKind;
}

interface IMenuCoords {
  top?: number;
  bottom?: number;
  left: number;
  resolvedPosition: TResolvedMenuPosition;
}

const MENU_GAP = 4;
const VIEWPORT_PADDING = 8;
const FALLBACK_MENU_WIDTH = 220;
const FALLBACK_MENU_HEIGHT = 280;

const ButtonWithDropdown: React.FC<IButtonWithDropdown> = ({
  options,
  menuPosition = "auto",
  columns = 1,
  mobileColumns = 1,
  active = false,
  highlight = false,
  children = null,
  kind = EButtonKind.NORMAL,
  ...props
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [, startTransition] = useTransition();
  const { isMobile } = useMobile();

  const {
    icon = <MdMoreHoriz size={ICON_SIZES.SM} />,
    tooltip = "",
    hide = false,
    className = "",
    text = "",
    isLoading = false,
    disabled = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<IMenuCoords | null>(null);

  const ref = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const menuItems = useMemo(() => {
    return options.filter((option) => !option?.hide);
  }, [options]);

  const closeMenuSafely = () => {
    requestAnimationFrame(() => {
      setIsOpen(false);
    });
  };

  const calculateMenuPosition = () => {
    const buttonEl = ref.current;
    if (!buttonEl) return;

    const rect = buttonEl.getBoundingClientRect();

    const menuRect = menuRef.current?.getBoundingClientRect();
    const menuWidth = menuRect?.width || FALLBACK_MENU_WIDTH;
    const menuHeight = menuRect?.height || FALLBACK_MENU_HEIGHT;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;

    let resolvedPosition: TResolvedMenuPosition = "bottom";

    if (menuPosition === "top" || menuPosition === "bottom") {
      resolvedPosition = menuPosition;
    } else {
      const fitsBelow = spaceBelow >= menuHeight;
      const fitsAbove = spaceAbove >= menuHeight;

      if (fitsBelow) {
        resolvedPosition = "bottom";
      } else if (fitsAbove) {
        resolvedPosition = "top";
      } else {
        resolvedPosition = spaceBelow >= spaceAbove ? "bottom" : "top";
      }
    }

    let left = rect.right - menuWidth;

    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    if (left + menuWidth > viewportWidth - VIEWPORT_PADDING) {
      left = Math.max(
        VIEWPORT_PADDING,
        viewportWidth - menuWidth - VIEWPORT_PADDING,
      );
    }

    if (resolvedPosition === "bottom") {
      const top = Math.min(
        rect.bottom + MENU_GAP,
        viewportHeight - VIEWPORT_PADDING,
      );

      setCoords({
        top,
        left,
        resolvedPosition,
      });
    } else {
      const bottom = Math.min(
        viewportHeight - rect.top + MENU_GAP,
        viewportHeight - VIEWPORT_PADDING,
      );

      setCoords({
        bottom,
        left,
        resolvedPosition,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    const body = document.body;

    if (isOpen) {
      body.classList.add("expenser-block-scroll");
    }

    return () => {
      body.classList.remove("expenser-block-scroll");
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    calculateMenuPosition();
  }, [isOpen, menuPosition, columns, mobileColumns, menuItems.length]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const raf = requestAnimationFrame(() => {
      calculateMenuPosition();
    });

    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleReposition = () => {
      calculateMenuPosition();
    };

    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, menuPosition]);

  if (hide) return null;

  return (
    <>
      <div
        ref={ref}
        className={clsx([
          "relative",
          "text-base",
          "text-gray-500",
          "dark:text-gray-300",
        ])}
      >
        <Button
          type="button"
          onClick={(e: any) => {
            e?.stopPropagation?.();
            e?.preventDefault?.();
            startTransition(() => {
              setIsOpen((v) => !v);
            });
          }}
          tooltip={tooltip}
          icon={icon}
          className={className}
          text={text}
          isLoading={isLoading}
          disabled={isLoading || disabled}
          active={active}
          highlight={highlight}
          kind={kind}
        />
      </div>

      {isOpen &&
        createPortal(
          <>
            <div
              tabIndex={-1}
              className={clsx([
                "fixed",
                "inset-0",
                "bg-transparent",
                "z-[9998]",
                "pointer-events-auto",
              ])}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeMenuSafely();
              }}
            />
            <div
              className={clsx([
                "block",
                "z-[9999]",
                "bg-white",
                "dark:bg-gray-900",
                "md:border dark:border-gray-800",
                "rounded md:rounded",
                "overflow-x-hidden",
                "overflow-y-auto",
                "no-scrollbar",
                "shadow",
                "dark:shadow-gray-950",
                "min-w-[190px]",
                "w-[max-content]",
                "max-w-[90vw]",
                "max-h-[60vh]",
                "md:max-h-[70vh]",
                "pointer-events-auto",
              ])}
              ref={menuRef}
              style={
                {
                  position: "fixed",
                  top: coords?.top,
                  bottom: coords?.bottom,
                  left: coords?.left ?? VIEWPORT_PADDING,
                  "--cols-md": columns,
                  "--cols": mobileColumns,
                } as CSSProperties
              }
            >
              {!isMobile && (
                <>
                  {children && (
                    <div className={clsx(["md:p-3", "px-3", "pt-4"])}>
                      {children}
                    </div>
                  )}
                </>
              )}
              <ul
                onPointerDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className={clsx([
                  "grid",
                  `[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]`,
                  `md:[grid-template-columns:repeat(var(--cols-md),minmax(0,1fr))]`,
                ])}
                role="menu"
              >
                {menuItems.map((option, index) => (
                  <motion.li
                    key={index}
                    onClick={(e: any) =>
                      startTransition(() => {
                        if (!option?.disabled) {
                          option?.onClick?.(e);
                          if (!option?.keepOpenOnClick) {
                            closeMenuSafely();
                          }
                        }
                      })
                    }
                    {...(!prefersReducedMotion && {
                      initial: {
                        opacity: 0,
                        y: coords?.resolvedPosition === "top" ? 10 : -10,
                      },
                      animate: { opacity: 1, y: 0 },
                    })}
                    // transition={{
                    //   duration: 0.09,
                    //   ease: "easeOut",
                    //   // delay: index * 0.05,
                    // }}
                    title={option?.tooltip || ""}
                    className={clsx([
                      "px-3 pr-4 md:px-3 md:pr-6",
                      "py-3 md:py-3",
                      "whitespace-nowrap",
                      {
                        "border-b border-b-gray-50":
                          index < menuItems.length - 1,
                        "dark:border-b-gray-800": true,
                        "dark:hover:bg-gray-700 hover:bg-emerald-100 hover:text-black hover:dark:text-white hover:rounded":
                          !option?.disabled,
                        "cursor-pointer": !option?.disabled,
                        "cursor-not-allowed text-gray-400": option?.disabled,
                        "dark:bg-gray-700 bg-emerald-100 text-black dark:text-white rounded":
                          option?.active,
                      },
                      "flex",
                      "flex-row",
                      "gap-0",
                      "md:gap-0",
                      "items-center",
                      "text-xs",
                      "md:text-sm",
                      option?.className || "",
                      "group",
                      "relative",
                    ])}
                    role="menuitem"
                  >
                    <span
                      className={clsx([
                        "shrink-0",
                        "min-w-[34px]",
                        "max-w-[34px]",
                        "dark:text-cyan-400 text-emerald-500",
                        {
                          "opacity-20": option?.disabled,
                          "group-hover:animate-pulse": !option?.disabled,
                        },
                      ])}
                    >
                      {option.icon}
                    </span>

                    <div className="truncate">{option.text}</div>
                  </motion.li>
                ))}
              </ul>
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default memo(ButtonWithDropdown);
